import {
  div,
  input,
  main,
  footer,
  p,
  button,
  route,
  routeSwitch,
  setRootPath,
  span,
  goTo,
  hmrRoot
} from '@srfnstack/fntags'
import './style.css'
import { user, refreshCredits, showNotification, checkSession, loadingSession } from './state.js'
import { connectEvents } from './events.js'
import { Navbar } from './components/Navbar.js'
import { Sidebar } from './components/Sidebar.js'
import { ModalProvider } from './components/ModalProvider.js'
import { ToastProvider } from './components/ToastProvider.js'
import { GenerateArtwork } from './pages/GenerateArtwork/index.js'
import { BuyCredits } from './pages/BuyCredits.js'
import { Checkout } from './pages/Checkout.js'
import { DesignSeedDetail } from './pages/DesignSeedDetail.js'
import { ArtworkReview } from './pages/ArtworkReview.js'
import { ArtworkReviewDetail } from './pages/ArtworkReviewDetail/index.js'
import { ProductReview } from './pages/ProductReview.js'
import { ProductReviewDetail } from './pages/ProductReviewDetail/index.js'
import { MockupAudit } from './pages/MockupAudit.js'
import { PinQueue } from './pages/PinQueue.js'
import { PinterestStats } from './pages/PinterestStats.js'
import { ProductInit } from './pages/ProductInit/index.js'
import { Queue } from './pages/Queue.js'
import { Finance } from './pages/Finance.js'
import { Orders } from './pages/Orders.js'
import { Settings } from './pages/Settings.js'
import { Graveyard } from './pages/Graveyard.js'
import { Tenants } from './pages/Tenants.js'
import { Tags } from './pages/Tags.js'
import { BulkOperations } from './pages/BulkOperations.js'
import { Login } from './pages/Login.js'
import { Signup } from './pages/Signup.js'
import { Help } from './pages/Help.js'
import { IntegrationConnect } from './pages/IntegrationConnect.js'
import { AutopilotQueue } from './pages/AutopilotQueue.js'
import { Governance } from './pages/Governance.js'
import { AutopilotDecisions } from './pages/AutopilotDecisions.js'
import { GateLabeling } from './pages/GateLabeling.js'
import { AutopilotPipeline } from './pages/AutopilotPipeline.js'
import { ArtworkApproval } from './pages/ArtworkApproval.js'

// --- Global Styles for Drag & Drop ---
const style = document.createElement('style')
style.textContent = `
  .sortable-fallback {
    opacity: 1 !important;
    z-index: 9999 !important;
    pointer-events: none;
    box-shadow: 0 30px 60px -12px rgb(0 0 0 / 0.75);
    transform: scale(1.05) !important;
    background: var(--color-base-200);
    border-radius: 0.75rem;
    overflow: hidden;
  }
  .sortable-ghost {
    opacity: 0.05 !important;
    background: var(--color-primary) !important;
  }
  .sortable-chosen {
    transform: scale(1.05);
    box-shadow: 0 10px 15px -3px rgb(0 0 0 / 0.5);
    z-index: 40;
  }
  .sortable-drag {
    opacity: 0 !important;
  }
`
document.head.appendChild(style)

user.subscribe((u) => {
  if (u.loggedIn) {
    refreshCredits()
  }
})

// --- CSRF Fetch Interceptor ---
const originalFetch = window.fetch
window.fetch = async (url, options = {}) => {
  const method = options.method || 'GET'
  const isStateChanging = !['GET', 'HEAD', 'OPTIONS'].includes(method.toUpperCase())

  if (isStateChanging) {
    const csrfToken = document.cookie
      .split('; ')
      .find(row => row.startsWith('csrfToken='))
      ?.split('=')[1]

    if (csrfToken) {
      options.headers = {
        ...(options.headers || {}),
        'x-csrf-token': csrfToken
      }
    }
  }
  const res = await originalFetch(url, options)

  if (res.status === 402) {
    showNotification('Insufficient credits to perform this action.', 'warning', {
      text: 'Buy More Credits',
      href: '/billing/buy-credits'
    }, 'toast-center toast-middle')
  }

  return res
}

// --- App Layout ---
setRootPath('/')
connectEvents()
checkSession()

const App = () => loadingSession.bindAs(loading => {
  if (loading) {
    return div({ class: 'min-h-screen flex items-center justify-center bg-base-300' },
      div({ class: 'flex flex-col items-center gap-4' },
        span({ class: 'loading loading-spinner loading-lg text-primary' }),
        p({ class: 'text-sm font-bold opacity-50 uppercase tracking-widest' }, 'Restoring Session...')
      )
    )
  }

  return user.bindAs(u => {
    if (!u.loggedIn) {
      return u.showSignup ? Signup() : Login()
    }

    const ImpersonationBanner = () => user.bindAs(u => {
      if (!u.impersonating) return span()
      return div({
        class: 'w-full flex items-center justify-between gap-4 px-6 py-3 text-sm font-bold',
        style: 'background: #f59e0b; color: #1c1917;'
      },
      div({ class: 'flex items-center gap-2' },
        span({ class: 'material-symbols-rounded' }, 'warning'),
        span(`Viewing as tenant: ${u.impersonating.tenantName} (ID: ${u.impersonating.tenantId}) — You are in impersonation mode.`)
      ),
      button({
        class: 'btn btn-sm btn-neutral shrink-0',
        onclick: async () => {
          await fetch('/api/system/impersonate', { method: 'DELETE' })
          window.location.reload()
        }
      }, 'Exit Impersonation')
      )
    })

    return div({ class: 'drawer lg:drawer-open' },
      input({ id: 'app-drawer', type: 'checkbox', class: 'drawer-toggle' }),
      div({ class: 'drawer-content flex flex-col min-h-screen' },
        ImpersonationBanner(),
        Navbar(),
        main({ class: 'flex-grow' },
          routeSwitch(
            route({ path: '/', absolute: true }, () => GenerateArtwork()),
            route({ path: '/billing/buy-credits' }, () => BuyCredits()),
            route({ path: '/billing/checkout' }, () => Checkout()),
            route({ path: '/design-seeds/:id' }, () => DesignSeedDetail()),
            route({ path: '/artwork-review', absolute: true }, () => ArtworkReview()),
            route({ path: '/artwork-review/:promptId' }, () => ArtworkReviewDetail()),
            route({ path: '/product-init/:productId' }, () => ProductInit()),
            route({ path: '/product-review', absolute: true }, () => ProductReview()),
            route({ path: '/product-review/:productId' }, () => ProductReviewDetail()),
            route({ path: '/mockup-audit', absolute: true }, () => MockupAudit()),
            route({ path: '/pin-queue', absolute: true }, () => PinQueue()),
            route({ path: '/pinterest-stats', absolute: true }, () => PinterestStats()),
            route({
              path: '/bulk-operations',
              absolute: true
            }, () => user().is_super_admin ? BulkOperations() : div({ class: 'p-8' }, 'Forbidden')),
            route({ path: '/queue' }, () => user().is_super_admin ? Queue() : div({ class: 'p-8' }, 'Forbidden')),
            route({ path: '/finance' }, () => Finance()),
            route({ path: '/orders' }, () => Orders()),
            route({ path: '/settings', absolute: true }, () => Settings()),
            route({ path: '/integrations/connect/:target' }, () => IntegrationConnect()),
            route({ path: '/approval-queue' }, () => AutopilotQueue()),
            route({ path: '/governance' }, () => Governance()),
            route({ path: '/autopilot-decisions' }, () => AutopilotDecisions()),
            route({ path: '/gate-labeling' }, () => GateLabeling()),
            route({ path: '/autopilot-pipeline' }, () => AutopilotPipeline()),
            route({ path: '/artwork-approval' }, () => ArtworkApproval()),
            route({
              path: '/tenants',
              absolute: true
            }, () => user().is_super_admin ? Tenants() : div({ class: 'p-8' }, 'Forbidden')),
            route({ path: '/tags', absolute: true }, () => Tags()),
            route({ path: '/help', absolute: true }, () => Help()),
            route({ path: '/graveyard' }, () => Graveyard()),
            route({ path: '.*' }, () => {
              goTo('/')
              return div()
            })
          )
        ),
        footer({ class: 'footer footer-center p-4 bg-base-200 text-base-content border-t border-base-300' },
          div(p('EtherBuildr Automation © 2026 - Data Driven Print-on-Demand'))
        )
      ),
      Sidebar(),
      ModalProvider(),
      ToastProvider()
    )
  })
})

hmrRoot(document.getElementById('root'), App)
